/**
 * Market Data PDF Generator
 * 
 * Main orchestrator for generating PDF exports from the Market Data page.
 * Handles header, filters, charts, and tables.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { registerRobotoFont } from './robotoFont';
import { formatFiltersForPDF } from './filterSummaryFormatter';
import {
  drawProjectedPatientsChart,
  drawSharedAffiliationChart,
  drawSharedAffiliationByServiceLineChart,
  drawSharedPatientsChart,
  drawSharedPatientsServiceLineChart,
  drawPayorChart,
  drawAffiliationSummary,
  drawPageHeader,
  drawContinuationPage,
  drawPageFooterWithLegend,
  drawLocationTypeLegend,
  drawAffiliationLegend,
  drawPayorLegend,
  drawTitleAndImageSlide,
  drawTitleAndParagraphSlide,
  drawBlankSlide,
  drawExecSummarySlide,
  drawCustomPage,
  AFFILIATION_COLORS,
} from './chartRenderer';
import { PDF_SETTINGS, getSettingsByType, PAGE_HEADER_STYLES, PAGE_FOOTER_STYLES } from './pdfStyles';
import { addLogoToPDF, addFooterToAllPages } from './logoManager';

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'provider.location.city')
 */
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

/**
 * Extract text from a value that might be a React element
 */
const extractText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    // React element - try to get props.children or return empty
    if (value.props?.children) {
      if (typeof value.props.children === 'string') return value.props.children;
      if (Array.isArray(value.props.children)) {
        return value.props.children.map(extractText).join(' ');
      }
    }
    return '';
  }
  return String(value);
};

/**
 * Get human-readable label for patient type
 */
const getPatientTypeLabel = (patientType) => {
  const labels = {
    'ALL_PATIENTS': 'Projected Patients',
    'PEDIATRIC_PATIENTS': 'Captured Pediatric Patients',
    'CAPTURED_CLAIMS': 'Captured Claims',
  };
  return labels[patientType] || patientType || 'Projected Patients';
};

/**
 * Render filter summary in a compact two-column layout
 */
const renderFilterSummary = (doc, filterLines, startY, pageWidth) => {
  if (!filterLines.length) return startY;
  
  const x = PDF_SETTINGS.margin.left;
  const col2X = pageWidth / 2;
  let y = startY;
  
  doc.setFontSize(PDF_SETTINGS.header.filterFontSize);
  
  filterLines.forEach((filter, idx) => {
    const colX = idx % 2 === 0 ? x : col2X;
    
    // Label (bold)
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_SETTINGS.colors.text.secondary);
    doc.text(`${filter.label}:`, colX, y);
    
    // Value (normal)
    const labelWidth = doc.getTextWidth(`${filter.label}: `);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_SETTINGS.colors.text.primary);
    
    // Truncate long values
    let value = filter.value;
    const maxValueWidth = (pageWidth / 2) - labelWidth - 20;
    while (doc.getTextWidth(value) > maxValueWidth && value.length > 10) {
      value = value.substring(0, value.length - 4) + '...';
    }
    
    doc.text(value, colX + labelWidth, y);
    
    // Move to next row after every 2 items
    if (idx % 2 === 1) {
      y += 14;
    }
  });
  
  // Handle odd number of filters
  if (filterLines.length % 2 === 1) {
    y += 14;
  }
  
  return y;
};

/**
 * Build a dynamic report title based on report type and filters
 * This is exported so the dialog can compute the same title
 * 
 * @param {Object} config - Configuration with title, reportType, filters, options
 * @returns {string} - The computed title
 */
export const buildReportTitle = (config) => {
  const {
    title = 'Market Data Report',
    reportType = 'projected_patients',
    filters = {},
    options = {},
  } = config;

  const {
    patientType = 'ALL_PATIENTS',
    claimsType = 'providers',
  } = options;

  // Only auto-generate if no title provided or if it's one of the exact default placeholders
  const defaultTitles = ['Market Data Export', 'Market Data Report', 'Market Data - Projected Patients', ''];
  const shouldAutoGenerateTitle = !title || defaultTitles.includes(title) || title.startsWith('Market Data');
  
  if (!shouldAutoGenerateTitle) {
    return title;  // Use custom title as-is
  }

  const typeLabel = claimsType === 'organizations' ? 'Organizations' : 'Providers';
  
  let effectivePatientType = filters.patientType || patientType;
  if (!effectivePatientType || effectivePatientType === 'ALL_PATIENTS') {
    if (reportType === 'captured_claims') {
      effectivePatientType = 'CAPTURED_CLAIMS';
    }
  }
  const patientsLabel = getPatientTypeLabel(effectivePatientType);
  const serviceLine = filters.serviceLine?.name || filters.serviceLine?.label || 
    (typeof filters.serviceLine === 'string' ? filters.serviceLine : '');
  
  let market = '';
  if (filters.market) {
    if (typeof filters.market === 'string') {
      market = filters.market;
    } else {
      market = filters.market.name || filters.market.label || 
               filters.market.market_name || filters.market.display_name ||
               filters.market.title || '';
    }
  }
  
  let dynamicTitle;
  if (reportType === 'payors' || reportType === 'payor_mix') {
    dynamicTitle = 'Payors - ' + patientsLabel;
  } else if (reportType === 'shared_affiliation') {
    dynamicTitle = 'Shared Affiliation - ' + patientsLabel;
  } else {
    dynamicTitle = patientsLabel;
  }
  
  const isCapturedClaimsByOrg = (reportType === 'captured_claims' || effectivePatientType === 'CAPTURED_CLAIMS') && claimsType === 'organizations';
  if (!isCapturedClaimsByOrg && serviceLine && serviceLine !== 'all' && serviceLine !== 'All Service Lines') {
    dynamicTitle += ` in ${serviceLine}`;
  }
  if (market) {
    dynamicTitle += ` in ${market}`;
  }
  // Note: "by Providers"/"by Organizations" intentionally omitted from title
  
  return dynamicTitle;
};

/**
 * Render a custom page based on its type or components
 * @param {Object} doc - jsPDF document
 * @param {Object} pageConfig - Custom page configuration
 * @param {string} pdfType - 'printable' or 'slideshow'
 * @param {boolean} includeLogo - Whether to include logo
 * @param {number} pageWidth - Page width in points
 */
const renderCustomPage = async (doc, pageConfig, pdfType, includeLogo, pageWidth) => {
  const {
    type,
    title = '',
    subtitle = '',
    content = '',
    imageData = null,
    metrics = [],
    components = [],  // New component-based structure
  } = pageConfig;

  // If page has components array, use the new component-based renderer
  if (components && components.length > 0) {
    drawCustomPage(doc, pageConfig, pdfType);
  } else if (type) {
    // Legacy: use old type-based rendering
    const slideOptions = {
      title,
      subtitle,
      content,
      imageData,
      metrics,
      pdfType,
    };

    switch (type) {
      case 'title_image':
        await drawTitleAndImageSlide(doc, slideOptions);
        break;
      case 'title_paragraph':
        drawTitleAndParagraphSlide(doc, slideOptions);
        break;
      case 'exec_summary':
        drawExecSummarySlide(doc, slideOptions);
        break;
      case 'blank':
      default:
        drawBlankSlide(doc, slideOptions);
        break;
    }
  } else {
    // No components and no type - just draw header
    drawCustomPage(doc, pageConfig, pdfType);
  }

  // Add logo if requested
  if (includeLogo) {
    const logoMaxWidth = pdfType === 'slideshow' ? 120 : 90;
    const logoMaxHeight = pdfType === 'slideshow' ? 30 : 22;
    const logoX = pageWidth - 15 - logoMaxWidth;
    const logoY = 5;
    await addLogoToPDF(doc, { 
      x: logoX, 
      y: logoY,
      maxWidth: logoMaxWidth,
      maxHeight: logoMaxHeight,
    });
  }
};

/**
 * Main PDF generation function for Market Data reports
 * 
 * @param {Object} config - Configuration object
 * @returns {Promise<jsPDF>} - The generated PDF document
 */
export const generateMarketDataPDF = async (config) => {
  const {
    // Report metadata
    title = 'Market Data Report',
    subtitle = '',              // Optional subtitle
    reportType = 'projected_patients',
    
    // Data
    chartData = [],           // Array from CLAIMS_PER_PROVIDER or similar
    providerCounts = null,    // { total, total_affiliated }
    tableData = [],           // For table-based reports
    columns = [],             // DynamicTable column definitions
    payors = [],              // For payors chart - array of {id, name, color}
    
    // Filters
    filters = {},
    
    // Options
    options = {},
    
    // Custom pages to insert (new feature)
    customPages = [],         // Array of { type, position, title, subtitle, content, imageData, metrics }
  } = config;

  const {
    orientation = 'landscape',
    pageSize = 'letter',
    includeHeader = true,
    includeLogo = true,  // Controlled separately or follows includeHeader
    includeFooter = true, // New option for footer
    includeChart = true,
    includeTable = false,
    maxRows = 200,
    patientType = 'ALL_PATIENTS',
    claimsType = 'providers',
    pdfType = 'printable',  // 'slideshow' or 'printable'
    useStyledHeader = true, // Use the new dark blue header with filter pills
    selectedColumns = null, // Column visibility selection from PDFExportDialog
    metric = 'claims',      // Captured-claims metric: 'claims' or 'value' (reimbursement $)
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);

  // Create PDF document
  // Slideshow = landscape, Printable = portrait (8.5x11)
  const effectiveOrientation = pdfType === 'printable' ? 'portrait' : orientation;
  const doc = new jsPDF({
    orientation: effectiveOrientation,
    unit: 'pt',
    format: pageSize,
  });

  // Register Roboto font
  registerRobotoFont(doc);
  doc.setFont('Roboto', 'normal');

  const pageWidth = doc.internal.pageSize.width;
  let yPosition = settings.margin.top;

  // Use the shared title-building function
  const dynamicTitle = buildReportTitle({ title, reportType, filters, options });
  const typeLabel = claimsType === 'organizations' ? 'Organizations' : 'Providers';

  // === CUSTOM PAGES (before data) ===
  // Render any custom pages that should appear before the main data
  // These are rendered FIRST, before the main report header
  const beforePages = (customPages || []).filter(p => p.position === 'before' || p.position === 'start');
  for (let i = 0; i < beforePages.length; i++) {
    const customPage = beforePages[i];
    // First custom page uses the initial blank page, subsequent ones add new pages
    if (i > 0) {
      doc.addPage();
    }
    await renderCustomPage(doc, customPage, pdfType, includeLogo, pageWidth);
  }
  
  // If we had "before" pages, add a new page for the main content
  if (beforePages.length > 0) {
    doc.addPage();
  }

  // Build filter pills for styled header
  // type: 'filled' = blue background, 'outlined' = white border only
  const buildFilterPills = () => {
    const pills = [];
    
    // Market/location - first pill, filled style
    let market = '';
    if (filters.market) {
      market = typeof filters.market === 'string' ? filters.market : 
               (filters.market.name || filters.market.label || '');
    }
    if (market) {
      pills.push({ text: market, type: 'filled' });
    }
    
    // Care Setting - second pill, filled style
    if (filters.careSetting && filters.careSetting !== 'All Care Settings' && filters.careSetting !== 'All') {
      pills.push({ text: filters.careSetting, type: 'filled' });
    }
    
    // Specialties - outlined style
    if (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') {
      pills.push({ text: filters.specialty, type: 'outlined' });
    } else if (filters.specialties && Array.isArray(filters.specialties) && filters.specialties.length > 0) {
      const specNames = filters.specialties.map(s => s.name || s.label || s).filter(Boolean);
      if (specNames.length <= 5) {
        specNames.forEach(name => pills.push({ text: name, type: 'outlined' }));
      } else {
        pills.push({ text: `${specNames.length} Specialties`, type: 'outlined' });
      }
    } else if (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length > 0) {
      const taxCount = filters.taxonomies.length;
      if (taxCount <= 5) {
        const taxNames = filters.taxonomies.map(t => t.name || t.label || t.classification || t).filter(Boolean);
        taxNames.forEach(name => pills.push({ text: String(name), type: 'outlined' }));
      } else {
        pills.push({ text: `${taxCount} Specialties`, type: 'outlined' });
      }
    }
    
    // Affiliation - outlined style
    if (filters.affiliation === 'affiliated') {
      pills.push({ text: 'Affiliated', type: 'outlined' });
    } else if (filters.affiliation === 'non-affiliated') {
      pills.push({ text: 'Non-Affiliated', type: 'outlined' });
    }
    
    // Payors - outlined style
    if ((reportType === 'payors' || reportType === 'payor_mix') && payors && payors.length > 0) {
      const payorNames = payors.map(p => p.name).filter(Boolean);
      if (payorNames.length <= 3) {
        payorNames.forEach(name => pills.push({ text: name, type: 'outlined' }));
      } else {
        pills.push({ text: `${payorNames.length} Payors`, type: 'outlined' });
      }
    }
    
    // Claims Date - outlined style
    if (filters.claimsDate !== undefined && filters.claimsDate !== null) {
      pills.push({ text: filters.claimsDate ? 'Before' : 'After', type: 'outlined' });
    }
    
    return pills;
  };

  // === HEADER ===
  if (includeHeader) {
    if (useStyledHeader) {
      // Draw gradient background with angled white section
      const headerResult = drawPageHeader(doc, dynamicTitle, subtitle, [], { pdfType });
      // Handle both old (number) and new (object) return values
      yPosition = typeof headerResult === 'object' ? headerResult.y : headerResult;
      
      // Add logo to top-right corner (in the white angled section)
      if (includeLogo) {
        const logoMaxWidth = pdfType === 'slideshow' ? 120 : 90;
        const logoMaxHeight = pdfType === 'slideshow' ? 30 : 22;
        const logoX = pageWidth - 15 - logoMaxWidth;  // 15px margin from right
        const logoY = 5;  // Near top of page in white section
        await addLogoToPDF(doc, { 
          x: logoX, 
          y: logoY,
          maxWidth: logoMaxWidth,
          maxHeight: logoMaxHeight,
        });
      }
    } else {
      // LEGACY HEADER - simple title and subtitle
      if (includeLogo) {
        const logoMaxWidth = settings.logo.maxWidth;
        const logoMaxHeight = settings.logo.maxHeight;
        const logoX = pageWidth - settings.margin.right - logoMaxWidth;
        await addLogoToPDF(doc, { 
          x: logoX, 
          y: yPosition - 15,
          maxWidth: logoMaxWidth,
          maxHeight: logoMaxHeight,
        });
        yPosition += settings.logo.maxHeight - 5;
      }

      // Auto-scale title font size to fit within 80% of page width
      const maxTitleWidth = pageWidth * 0.80;
      const maxTitleFontSize = settings.header.titleFontSize;
      let titleFontSize = maxTitleFontSize;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(titleFontSize);
      let titleWidth = doc.getTextWidth(dynamicTitle);
      
      // Scale down font size if title is too wide
      while (titleWidth > maxTitleWidth && titleFontSize > 10) {
        titleFontSize -= 1;
        doc.setFontSize(titleFontSize);
        titleWidth = doc.getTextWidth(dynamicTitle);
      }
      
      doc.setTextColor(30, 30, 30);
      doc.text(dynamicTitle, settings.margin.left, yPosition);
      yPosition += titleFontSize + 4;

      // Build subtitle
      const subtitleParts = [];
      
      if (filters.careSetting && filters.careSetting !== 'All Care Settings' && filters.careSetting !== 'All') {
        subtitleParts.push(filters.careSetting);
      }
      
      if (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') {
        subtitleParts.push(filters.specialty);
      } else if (filters.specialties && Array.isArray(filters.specialties) && filters.specialties.length > 0) {
        const specNames = filters.specialties.map(s => s.name || s.label || s).filter(Boolean);
        if (specNames.length > 0 && specNames.length <= 3) {
          subtitleParts.push(specNames.join(', '));
        } else if (specNames.length > 3) {
          subtitleParts.push(`${specNames.length} Specialties`);
        }
      } else if (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length > 0) {
        const taxCount = filters.taxonomies.length;
        if (taxCount === 1) {
          const tax = filters.taxonomies[0];
          const taxName = tax.name || tax.label || tax.classification || tax;
          subtitleParts.push(typeof taxName === 'string' ? taxName : `${taxCount} Specialty`);
        } else if (taxCount <= 3) {
          const taxNames = filters.taxonomies.map(t => t.name || t.label || t.classification || t).filter(Boolean);
          subtitleParts.push(taxNames.join(', '));
        } else {
          subtitleParts.push(`${taxCount} Specialties`);
        }
      }
      
      if (filters.affiliation) {
        if (filters.affiliation === 'affiliated') {
          subtitleParts.push('Affiliated Only');
        } else if (filters.affiliation === 'non-affiliated') {
          subtitleParts.push('Non-Affiliated Only');
        }
      }
      
      if ((reportType === 'payors' || reportType === 'payor_mix') && payors && payors.length > 0) {
        const payorNames = payors.map(p => p.name).filter(Boolean);
        if (payorNames.length > 0 && payorNames.length <= 3) {
          subtitleParts.push(payorNames.join(', '));
        } else if (payorNames.length > 3) {
          subtitleParts.push(`${payorNames.length} Payors`);
        }
      }
      
      if (filters.claimsDate !== undefined && filters.claimsDate !== null) {
        subtitleParts.push(filters.claimsDate ? 'Claims Before' : 'Claims After');
      }
      
      if (filters.tag) {
        const tagName = filters.tag.name || filters.tag.label || (typeof filters.tag === 'string' ? filters.tag : '');
        if (tagName) {
          subtitleParts.push(`Tag: ${tagName}`);
        }
      }
      
      const isCapturedClaimsByOrg = (reportType === 'captured_claims' || filters.patientType === 'CAPTURED_CLAIMS') 
                                    && claimsType === 'organizations';
      if (filters.serviceLine && !isCapturedClaimsByOrg) {
        const slName = filters.serviceLine.name || filters.serviceLine.label || 
          (typeof filters.serviceLine === 'string' ? filters.serviceLine : '');
        if (slName && slName !== 'all' && slName !== 'All Service Lines' && slName !== 'All') {
          if (!subtitleParts.some(p => p.includes(slName))) {
            subtitleParts.push(slName);
          }
        }
      }
      
      let subtitle = '';
      if (subtitleParts.length > 0) {
        subtitle = 'By ' + subtitleParts.join(' | ');
      } else {
        subtitle = 'By All Care Settings and All Specialties';
      }
      
      doc.setFontSize(settings.header.subtitleFontSize);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(subtitle, settings.margin.left + 10, yPosition + 5);
      yPosition += settings.header.subtitleFontSize + 10;  // Reduced from +20
    }
  } else {
    yPosition += 10;  // Reduced from 20
  }

  // Page-1 filter context strip (Shared Patients only).
  // A light gray bar below the green header showing the filters in effect:
  // "Service line: All  ·  Specialty: All  ·  Patients: Projected  ·  Affiliation: All"
  // Pages 2+ skip this (they use drawContinuationPage).
  if (reportType === 'shared_patients' && includeHeader) {
    const pairs = [];

    let patientsValue = 'Projected';
    if (filters.patientType === 'PEDIATRIC_PATIENTS') patientsValue = 'Pediatric';
    else if (filters.patientType === 'CAPTURED_CLAIMS') patientsValue = 'Captured Claims';

    if (claimsType === 'servicelines') {
      // Drill-down (one provider pair, broken out by service line): the
      // Service line / Specialty / Affiliation filters don't apply here.
      pairs.push({ label: 'Direction', value: filters.before ? 'Before' : 'After' });
      pairs.push({ label: 'Patients', value: patientsValue });
    } else {
      const slName = filters.serviceLine?.name || filters.serviceLine?.label || null;
      pairs.push({ label: 'Service line', value: slName || 'All' });

      let specialtyValue = 'All';
      if (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length > 0) {
        if (filters.taxonomies.length === 1) {
          const tax = filters.taxonomies[0];
          specialtyValue = tax.name || tax.label || tax.classification || 'All';
        } else {
          specialtyValue = `${filters.taxonomies.length} selected`;
        }
      }
      pairs.push({ label: 'Specialty', value: specialtyValue });

      pairs.push({ label: 'Patients', value: patientsValue });

      let affiliationValue = 'All';
      if (filters.affiliation === 'affiliated') affiliationValue = 'Affiliated';
      else if (filters.affiliation === 'non-affiliated') affiliationValue = 'Non-Affiliated';
      pairs.push({ label: 'Affiliation', value: affiliationValue });
    }

    const stripX = 20;
    const stripY = yPosition;
    const stripWidth = pageWidth - 40;
    const stripHeight = pdfType === 'slideshow' ? 22 : 18;

    // Light gray-blue background, rounded corners (matches table header tone)
    doc.setFillColor(243, 247, 251);
    doc.roundedRect(stripX, stripY, stripWidth, stripHeight, 3, 3, 'F');

    const fontSize = pdfType === 'slideshow' ? 9 : 8;
    const textY = stripY + stripHeight / 2 + 3;
    let textX = stripX + 12;

    pairs.forEach((pair, idx) => {
      if (idx > 0) {
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(fontSize);
        doc.setTextColor(156, 163, 175);  // light gray dot
        const sep = '  ·  ';  // middle-dot
        doc.text(sep, textX, textY);
        textX += doc.getTextWidth(sep);
      }

      // Label (lighter)
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(107, 114, 128);
      const labelText = `${pair.label}: `;
      doc.text(labelText, textX, textY);
      textX += doc.getTextWidth(labelText);

      // Value (bold, darker)
      doc.setFont('Roboto', 'bold');
      doc.setFontSize(fontSize);
      doc.setTextColor(55, 65, 81);
      doc.text(pair.value, textX, textY);
      textX += doc.getTextWidth(pair.value);
    });

    yPosition = stripY + stripHeight + 4;
  }

  // Note: Affiliation summary badges removed - legend is now at bottom of table

  // === CHART (with pagination) ===
  // Ensure chartData is an array - extract rows if needed
  let chartRows = chartData;
  if (!Array.isArray(chartData)) {
    chartRows = chartData?.rows || chartData?.data?.rows || [];
  }
  
  // Rows per page: 10 for slideshow, 25 for printable.
  // For shared_patients the row height is trimmed (see drawSharedPatientsChart)
  // so a full page of rows still fits on page 1 alongside the filter strip —
  // this keeps a default 25-row export on a single page (no orphan row).
  const rowsPerPage = pdfType === 'slideshow' ? 10 : 25;

  // Limit to maxRows total
  const limitedRows = chartRows.slice(0, maxRows);

  if (includeChart && limitedRows.length > 0) {
    // Split into pages
    const totalPages = Math.ceil(limitedRows.length / rowsPerPage);

    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      const startIdx = pageNum * rowsPerPage;
      const endIdx = Math.min(startIdx + rowsPerPage, limitedRows.length);
      const pageRows = limitedRows.slice(startIdx, endIdx);
      
      // For pages after the first, add new page and draw header
      if (pageNum > 0) {
        doc.addPage();
        // For shared_patients, suppress the repeated title header on pages 2+
        // and use the continuation-page frame (gradient + logo, no title).
        if (reportType === 'shared_patients') {
          yPosition = drawContinuationPage(doc, { pdfType });
        } else {
          // Use same header with title/subtitle on all pages
          const headerResult = drawPageHeader(doc, dynamicTitle, subtitle, [], { pdfType });
          yPosition = typeof headerResult === 'object' ? headerResult.y : headerResult;
        }

        // Add logo on all pages
        if (includeLogo) {
          const logoMaxWidth = pdfType === 'slideshow' ? 120 : 90;
          const logoMaxHeight = pdfType === 'slideshow' ? 30 : 22;
          const logoX = pageWidth - 15 - logoMaxWidth;
          const logoY = 5;
          await addLogoToPDF(doc, {
            x: logoX,
            y: logoY,
            maxWidth: logoMaxWidth,
            maxHeight: logoMaxHeight,
          });
        }
      }
      
      // Draw chart for this page's rows
      const isLastPage = pageNum === totalPages - 1;
      const chartX = useStyledHeader ? 20 : settings.margin.left;
      
      switch (reportType) {
        case 'projected_patients':
        case 'captured_claims':
          yPosition = drawProjectedPatientsChart(doc, pageRows, {
            x: chartX,
            y: yPosition,
            maxRows: pageRows.length,
            allData: limitedRows,  // Pass all data for consistent column widths across pages
            patientType,
            claimsType,
            showLegend: patientType !== 'CAPTURED_CLAIMS' && patientType !== 'PEDIATRIC_PATIENTS',  // Show on every page
            pdfType,
            filters,  // Pass filters to check for single specialty
            selectedColumns,  // Pass column visibility selection
            metric,  // Captured-claims metric (claims vs reimbursement value)
          });
          break;

        case 'shared_affiliation':
          if (claimsType === 'servicelines') {
            yPosition = drawSharedAffiliationByServiceLineChart(doc, pageRows, {
              x: chartX,
              y: yPosition,
              maxRows: pageRows.length,
              allData: limitedRows,
              showLegend: pageNum === 0,
              pdfType,
              filters,
              selectedColumns,
            });
          } else {
            yPosition = drawSharedAffiliationChart(doc, pageRows, {
              x: chartX,
              y: yPosition,
              maxRows: pageRows.length,
              allData: limitedRows,
              claimsType,
              showLegend: pageNum === 0,
              pdfType,
              filters,
              selectedColumns,  // Pass column visibility selection
            });
          }
          break;

        case 'shared_patients':
          if (claimsType === 'servicelines') {
            yPosition = drawSharedPatientsServiceLineChart(doc, pageRows, {
              x: chartX,
              y: yPosition,
              maxRows: pageRows.length,
              allData: limitedRows,
              pdfType,
              filters,
              selectedColumns,
            });
          } else {
            yPosition = drawSharedPatientsChart(doc, pageRows, {
              x: chartX,
              y: yPosition,
              maxRows: pageRows.length,
              allData: limitedRows,
              pdfType,
              filters,
              selectedColumns,
            });
          }
          break;

        case 'payors':
        case 'payor_mix':
          yPosition = drawPayorChart(doc, pageRows, payors, {
            x: chartX,
            y: yPosition,
            maxRows: pageRows.length,
            allData: limitedRows,
            showLegend: true,  // Show legend on every page
            claimsType,
            pdfType,
            filters,
            selectedColumns,  // Pass column visibility selection
          });
          break;
          
        default:
          yPosition = drawProjectedPatientsChart(doc, pageRows, {
            x: chartX,
            y: yPosition,
            maxRows: pageRows.length,
            allData: limitedRows,  // Pass all data for consistent column widths across pages
            patientType,
            claimsType,
            showLegend: isLastPage,
            pdfType,
            filters,  // Pass filters to check for single specialty
            selectedColumns,  // Pass column visibility selection
            metric,  // Captured-claims metric (claims vs reimbursement value)
          });
      }
    }
  }

  // === TABLE (for other report types or if explicitly requested) ===
  if (includeTable && tableData.length > 0 && columns.length > 0) {
    // Filter out columns that shouldn't be exported
    const exportColumns = columns.filter(col => 
      col.key && col.label && !col.hideInExport
    );

    // Format table headers
    const headers = exportColumns.map(col => col.label);

    // Format table body
    const body = tableData.slice(0, maxRows).map(row => {
      return exportColumns.map(col => {
        const value = getNestedValue(row, col.key);
        if (col.format) {
          return extractText(col.format(value, row));
        }
        return value ?? '';
      });
    });

    // Draw table using autoTable
    autoTable(doc, {
      startY: yPosition,
      head: [headers],
      body: body,
      styles: {
        fontSize: PDF_SETTINGS.table.fontSize,
        cellPadding: PDF_SETTINGS.table.cellPadding,
      },
      headStyles: {
        fillColor: PDF_SETTINGS.table.headerBgColor,
        textColor: PDF_SETTINGS.colors.text.white,
        fontStyle: 'bold',
        fontSize: PDF_SETTINGS.table.headerFontSize,
      },
      alternateRowStyles: {
        fillColor: PDF_SETTINGS.table.alternateBgColor,
      },
      showHead: 'everyPage',
      margin: {
        top: settings.margin.top,
        left: settings.margin.left,
        right: settings.margin.right,
      },
      didDrawPage: () => {}, // Footer added at the end
    });
  }

  // Add footer with logo and page numbers to all pages (if enabled)
  // Skip if using styled header - it has its own footer built in
  if (includeFooter && !useStyledHeader) {
    await addFooterToAllPages(doc, { pdfType });
  }

  // === CUSTOM PAGES (after data) ===
  // Render any custom pages that should appear after the main data
  const afterPages = (customPages || []).filter(p => p.position === 'after' || p.position === 'end');
  for (const customPage of afterPages) {
    doc.addPage();
    await renderCustomPage(doc, customPage, pdfType, includeLogo, pageWidth);
  }

  return doc;
};

/**
 * Generate and download PDF
 */
export const downloadMarketDataPDF = async (config) => {
  const doc = await generateMarketDataPDF(config);
  const filename = config.filename || `market-data-${Date.now()}.pdf`;
  doc.save(filename);
};

/**
 * Generate PDF and return as blob URL for preview
 */
export const previewMarketDataPDF = async (config) => {
  const doc = await generateMarketDataPDF(config);
  const blob = doc.output('blob');
  return URL.createObjectURL(blob);
};

export default {
  generateMarketDataPDF,
  downloadMarketDataPDF,
  previewMarketDataPDF,
};